import React from "react";

import _ from "lodash";

import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";

import { AutocompleteHookType, ElementType } from "commons/hooks/useAutocomplete";

import HtmlWrapper from "commons/ui/HtmlWrapper";
import InputText from "commons/ui/input/InputText";

import { cleanHtml, mapItalicTags } from "commons/utils/utils";

export type PropsType<T> = {
    label?: string;
    autocompleteHook: AutocompleteHookType<T>;
    upsideDown?: boolean;
    placeholder?: string;
    searchPlaceholder?: string;
    tooltip?: any;
    errors?: string[];
    onFocus?: CallableFunction;
    onBlur?: CallableFunction;
    tabIndex?: number;
    dontShowClearIcon?: boolean;
    startAdornment?: any;
    className?: string;
    filterInput?: boolean;
    required?: boolean;
    disabled?: boolean;
    ctaElement?: JSX.Element;
    CustomOptionList?: (props: {
        element: ElementType<T>;
        index: number;
        autocompleteHook: AutocompleteHookType<T>;
        optionRefs: React.MutableRefObject<Array<HTMLButtonElement | null>>;
        inputRef: any;
    }) => JSX.Element;
};

export const renderElementLabel = (elementLabel: string, searchValue: string) => {
    const searchBoldValue = elementLabel.substring(
        elementLabel.search(new RegExp(searchValue, "i")),
        searchValue.length + elementLabel.search(new RegExp(searchValue, "i"))
    );

    return searchBoldValue
        ? elementLabel.replace(new RegExp(searchValue, "i"), "<b>" + searchBoldValue + "</b>")
        : elementLabel;
};

function OptionList<T>({
    element,
    index,
    autocompleteHook,
    optionRefs,
    inputRef,
}: {
    element: ElementType<T>;
    index: number;
    autocompleteHook: AutocompleteHookType<T>;
    optionRefs: React.MutableRefObject<Array<HTMLButtonElement | null>>;
    inputRef: any;
}) {
    const { onItemSelected, closeSelection, searchValue } = autocompleteHook;

    const onItemSelectedAction = React.useCallback(
        () => onItemSelected(element),
        [onItemSelected, element]
    );

    const onOptionsKeyDown = React.useCallback(
        (event: any) => {
            if (optionRefs.current[index + 1]) {
                // Arrow Down
                if (event.keyCode === 40) {
                    optionRefs.current[index + 1]?.focus();
                }
            } else {
                // Tab
                if (event.keyCode === 9) {
                    closeSelection();
                }
            }

            // Arrow Up
            if (event.keyCode === 38) {
                if (optionRefs.current[index - 1]) {
                    optionRefs.current[index - 1]?.focus();
                }

                if (index === 0) {
                    inputRef.current.focus();
                }
            }
        },
        [closeSelection, optionRefs, inputRef, index]
    );

    return (
        <button
            type={"button"}
            data-testid={"item"}
            onClick={onItemSelectedAction}
            className={"option"}
            ref={(ref) => (optionRefs.current[index] = ref)}
            onKeyDown={onOptionsKeyDown}
        >
            <HtmlWrapper
                htmlString={`<p>${renderElementLabel(
                    mapItalicTags(cleanHtml(element.label)),
                    searchValue
                )}</p>`}
            />
        </button>
    );
}

function AutocompleteInput<T>({
    label,
    placeholder,
    searchPlaceholder,
    autocompleteHook,
    upsideDown,
    tooltip,
    errors,
    onFocus,
    onBlur,
    tabIndex,
    dontShowClearIcon = false,
    startAdornment,
    className,
    filterInput,
    required,
    ctaElement,
    disabled = false,
    CustomOptionList,
}: PropsType<T>) {
    const {
        open,
        searchValue,
        items,
        itemSelectedLabel,
        onAutocompleteFocus,
        onClearClicked,
        onSearchValueChange,
        closeSelection,
        inputRef,
        loading,
    } = autocompleteHook;

    const optionRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

    const onChange = React.useCallback(
        (event: any) => {
            onSearchValueChange(event.target.value);
        },
        [onSearchValueChange]
    );

    const onInputTextKeyDown = React.useCallback(
        (event: any) => {
            // Arrow Down
            if (event.keyCode === 40 && optionRefs.current[0]) {
                optionRefs.current[0].focus();
            }

            // Tab
            if (event.keyCode === 9) {
                closeSelection();
            }
        },
        [closeSelection]
    );

    return (
        <div className={`autocomplete-container ${open ? "container-open" : ""}`}>
            <InputText
                label={label}
                value={itemSelectedLabel}
                onFocus={onAutocompleteFocus}
                ctaElement={ctaElement}
                endAdornment={
                    !dontShowClearIcon && itemSelectedLabel ? (
                        <button
                            type={"button"}
                            data-testid={"clear-icon"}
                            className={"clear-autocomplete"}
                            onClick={onClearClicked}
                            disabled={disabled}
                        >
                            <ClearIcon titleAccess={"Cancella"} />
                        </button>
                    ) : null
                }
                tooltip={tooltip}
                errors={errors}
                tabIndex={tabIndex}
                placeholder={placeholder ?? ""}
                startAdornment={
                    itemSelectedLabel ? null : (
                        <div
                            className={`start-adornment ${disabled ? "disabled" : ""}`}
                            data-testid={"open-selection"}
                            onClick={onAutocompleteFocus}
                        >
                            {startAdornment ?? <SearchIcon titleAccess={"Cerca"} />}
                        </div>
                    )
                }
                className={className}
                filterInput={filterInput}
                required={required}
                disabled={disabled}
            />

            <div
                data-testid="close-selection"
                className={"fixed-layer"}
                style={{ display: open ? "block" : "none" }}
                onClick={closeSelection}
            ></div>

            <div className="options-relative">
                <div
                    className={`options${upsideDown ? "-upside-down" : ""} ${
                        errors && errors.length > 0 ? "has-errors" : ""
                    }`}
                    style={{ display: open ? "flex" : "none" }}
                >
                    <div className={"search-value-input-container"}>
                        <InputText
                            ref={inputRef}
                            id={"a2-input"}
                            placeholder={searchPlaceholder ?? ""}
                            value={searchValue}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            onChange={onChange}
                            onKeyDown={onInputTextKeyDown}
                        />
                    </div>

                    <div className={"options-container"}>
                        {items.length > 0 ? (
                            items.map((element, index) => {
                                let key = "";

                                if (_.isArray(element.value)) {
                                    key = element.value.join("-");
                                } else if (_.isObject(element.value)) {
                                    key = (element.value as any).id;
                                } else {
                                    key = JSON.stringify(element.value);
                                }

                                return CustomOptionList ? (
                                    <CustomOptionList
                                        key={key}
                                        element={element}
                                        index={index}
                                        autocompleteHook={autocompleteHook}
                                        optionRefs={optionRefs}
                                        inputRef={inputRef}
                                    />
                                ) : (
                                    <OptionList
                                        key={key}
                                        element={element}
                                        index={index}
                                        autocompleteHook={autocompleteHook}
                                        optionRefs={optionRefs}
                                        inputRef={inputRef}
                                    />
                                );
                            })
                        ) : (
                            <div key={"empty"} className={"option empty-list"}>
                                <p>{loading ? "Caricamento..." : "Nessun elemento trovato"}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AutocompleteInput;
